import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  LayoutGrid, Plus, Trash2, X, ChevronDown, Loader2,
  Calendar as CalendarIcon, ListChecks, Columns,
  GripVertical, Clock,
} from "lucide-react";
import { colors, fonts, KANBAN_CADENCE_STAGES, KANBAN_ITEM_TYPES, KANBAN_PRIORITIES } from "../styles/theme";
import { useAuth } from "../hooks/useAuth";
import {
  fetchKanbanBoards, createKanbanBoard, updateKanbanBoard, deleteKanbanBoard,
  fetchKanbanItems, createKanbanItem, updateKanbanItem, deleteKanbanItem,
} from "../services/api";
import CalendarGrid from "../components/CalendarGrid";
import { expandRecurrence, describeRecurrence, RECURRENCE_PRESETS } from "../utils/recurrence";

// ── Helpers ──────────────────────────────────────────────────

function dueDateColor(dueDate) {
  if (!dueDate) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diff = (due - now) / (1000 * 60 * 60 * 24);
  if (diff < 0) return "#ef4444";
  if (diff === 0) return "#f59e0b";
  if (diff <= 7) return "#3b82f6";
  return colors.textSecondary;
}

function fmtDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function fmtDateTime(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

// ── Main Component ───────────────────────────────────────────

export default function Kanban() {
  const { role } = useAuth();

  // Board state
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState("kanban"); // kanban | cadence | calendar

  // New board form
  const [showNewBoard, setShowNewBoard] = useState(false);
  const [newBoardName, setNewBoardName] = useState("");

  // Add item form
  const [addingInColumn, setAddingInColumn] = useState(null);
  const [addTitle, setAddTitle] = useState("");
  const [addType, setAddType] = useState("task");
  const [addDueDate, setAddDueDate] = useState("");

  // Detail modal
  const [editItem, setEditItem] = useState(null);

  // Drag state
  const [dragItem, setDragItem] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [dropIndex, setDropIndex] = useState(-1);
  const [moving, setMoving] = useState(null);
  const cardRefs = useRef({});

  const selectedBoard = boards.find((b) => b.id === selectedBoardId) || null;

  // Load boards
  useEffect(() => {
    setLoading(true);
    fetchKanbanBoards()
      .then((data) => {
        setBoards(data.boards || []);
        if (data.boards?.length) {
          setSelectedBoardId(data.boards[0].id);
          setViewMode(data.boards[0].viewType || "kanban");
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Load items when board changes
  useEffect(() => {
    if (!selectedBoardId) return;
    setItemsLoading(true);
    fetchKanbanItems(selectedBoardId)
      .then((data) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setItemsLoading(false));
  }, [selectedBoardId]);

  // Get columns based on view mode
  const columns = useMemo(() => {
    if (viewMode === "cadence") return KANBAN_CADENCE_STAGES;
    return selectedBoard?.columns || [];
  }, [viewMode, selectedBoard]);

  // Expand recurring items for calendar view
  const calendarItems = useMemo(() => {
    if (viewMode !== "calendar") return items;
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
    const expanded = [];
    for (const item of items) {
      if (item.recurrence) {
        const dates = expandRecurrence(item.recurrence, start, end);
        for (const d of dates) {
          expanded.push({ ...item, id: `${item.id}-${d.toISOString()}`, dueDate: d.toISOString(), _originalId: item.id });
        }
      } else if (item.dueDate) {
        expanded.push(item);
      }
    }
    return expanded;
  }, [items, viewMode]);

  // ── Board CRUD ─────────────────────────────────────────────

  const handleCreateBoard = async () => {
    if (!newBoardName.trim()) return;
    try {
      const board = await createKanbanBoard({ name: newBoardName.trim(), viewType: "kanban" });
      setBoards((prev) => [board, ...prev]);
      setSelectedBoardId(board.id);
      setNewBoardName("");
      setShowNewBoard(false);
    } catch {}
  };

  const handleDeleteBoard = async () => {
    if (!selectedBoardId || !window.confirm("Delete this board and all its items?")) return;
    try {
      await deleteKanbanBoard(selectedBoardId);
      setBoards((prev) => prev.filter((b) => b.id !== selectedBoardId));
      setItems([]);
      setSelectedBoardId(boards.length > 1 ? boards.find((b) => b.id !== selectedBoardId)?.id : null);
    } catch {}
  };

  // ── Item CRUD ──────────────────────────────────────────────

  const handleAddItem = async (columnId) => {
    if (!addTitle.trim() || !selectedBoardId) return;
    try {
      const item = await createKanbanItem(selectedBoardId, {
        title: addTitle.trim(),
        type: addType,
        columnId,
        dueDate: addDueDate || null,
      });
      setItems((prev) => [...prev, item]);
      setAddTitle("");
      setAddType("task");
      setAddDueDate("");
      setAddingInColumn(null);
    } catch {}
  };

  const handleUpdateItem = async (itemId, data) => {
    if (!selectedBoardId) return;
    try {
      const updated = await updateKanbanItem(selectedBoardId, itemId, data);
      setItems((prev) => prev.map((i) => (i.id === itemId ? updated : i)));
      if (editItem?.id === itemId) setEditItem(updated);
    } catch {}
  };

  const handleDeleteItem = async (itemId) => {
    if (!selectedBoardId || !window.confirm("Delete this item?")) return;
    try {
      await deleteKanbanItem(selectedBoardId, itemId);
      setItems((prev) => prev.filter((i) => i.id !== itemId));
      if (editItem?.id === itemId) setEditItem(null);
    } catch {}
  };

  // ── Drag Handlers ──────────────────────────────────────────

  const handleDragStart = useCallback((e, item) => {
    setDragItem(item);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", item.id);
    if (e.currentTarget) e.currentTarget.style.opacity = "0.5";
  }, []);

  const handleDragEnd = useCallback((e) => {
    if (e.currentTarget) e.currentTarget.style.opacity = "1";
    setDragItem(null);
    setDropTarget(null);
    setDropIndex(-1);
  }, []);

  const handleDragOver = useCallback(
    (e, columnId) => {
      if (!dragItem) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDropTarget(columnId);

      const colItems = items
        .filter((i) => i.columnId === columnId && i.id !== dragItem.id)
        .sort((a, b) => (a.position || 0) - (b.position || 0));

      let idx = colItems.length;
      for (let i = 0; i < colItems.length; i++) {
        const el = cardRefs.current[colItems[i].id];
        if (el) {
          const rect = el.getBoundingClientRect();
          if (e.clientY < rect.top + rect.height / 2) { idx = i; break; }
        }
      }
      setDropIndex(idx);
    },
    [dragItem, items],
  );

  const handleDragLeave = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
      setDropTarget(null);
      setDropIndex(-1);
    }
  }, []);

  const handleDrop = useCallback(
    async (e, toColumnId) => {
      e.preventDefault();
      setDropTarget(null);
      if (!dragItem) return;
      const item = dragItem;
      const targetIdx = dropIndex;
      setDragItem(null);
      setDropIndex(-1);
      setMoving(item.id);

      const colItems = items
        .filter((i) => i.columnId === toColumnId && i.id !== item.id)
        .sort((a, b) => (a.position || 0) - (b.position || 0));
      const insertAt = Math.min(targetIdx, colItems.length);
      colItems.splice(insertAt, 0, { ...item, columnId: toColumnId });

      const updates = colItems.map((ci, idx) => ({ id: ci.id, columnId: toColumnId, position: idx }));

      // Optimistic update
      setItems((prev) => prev.map((i) => {
        const upd = updates.find((u) => u.id === i.id);
        return upd ? { ...i, columnId: upd.columnId, position: upd.position } : i;
      }));

      try {
        await updateKanbanItem(selectedBoardId, item.id, { columnId: toColumnId, position: insertAt });
      } catch {
        const data = await fetchKanbanItems(selectedBoardId);
        setItems(data.items || []);
      } finally {
        setMoving(null);
      }
    },
    [dragItem, dropIndex, items, selectedBoardId],
  );

  // ── Render ─────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <Loader2 size={24} color={colors.accent} className="spin" />      </div>
    );
  }

  return (
    <div style={{ padding: "24px 32px", maxWidth: 1600 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <LayoutGrid size={22} color={colors.accent} />
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, fontFamily: fonts.heading, color: colors.text }}>Kanban</h1>

        {/* Board selector */}
        <select
          value={selectedBoardId || ""}
          onChange={(e) => {
            setSelectedBoardId(e.target.value);
            const b = boards.find((b) => b.id === e.target.value);
            if (b) setViewMode(b.viewType || "kanban");
          }}
          style={{
            padding: "6px 10px", fontSize: 13, border: `1px solid ${colors.border}`,
            borderRadius: 6, background: colors.bgCard, color: colors.text,
            fontFamily: fonts.body, cursor: "pointer", marginLeft: 8,
          }}
        >
          {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>

        <button
          onClick={() => setShowNewBoard(!showNewBoard)}
          style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 10px", borderRadius: 6, border: `1px solid ${colors.border}`, background: "transparent", color: colors.accent, fontSize: 12, cursor: "pointer", fontFamily: fonts.body }}
        >
          <Plus size={14} /> New Board
        </button>

        {selectedBoard && role === "admin" && (
          <button
            onClick={handleDeleteBoard}
            style={{ display: "flex", alignItems: "center", padding: "6px 8px", borderRadius: 6, border: `1px solid ${colors.border}`, background: "transparent", color: colors.textSecondary, cursor: "pointer" }}
            title="Delete board"
          >
            <Trash2 size={14} />
          </button>
        )}

        {/* View tabs */}
        <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
          {[
            { id: "kanban", label: "Kanban", icon: Columns },
            { id: "cadence", label: "Cadence", icon: ListChecks },
            { id: "calendar", label: "Calendar", icon: CalendarIcon },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setViewMode(id)}
              style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "6px 12px", fontSize: 12, fontWeight: 600,
                border: `1px solid ${viewMode === id ? colors.accent : colors.border}`,
                borderRadius: 6,
                background: viewMode === id ? colors.accent : "transparent",
                color: viewMode === id ? "#fff" : colors.textSecondary,
                cursor: "pointer", fontFamily: fonts.body,
              }}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* New board form */}
      {showNewBoard && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16, maxWidth: 400 }}>
          <input
            value={newBoardName}
            onChange={(e) => setNewBoardName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreateBoard()}
            placeholder="Board name..."
            autoFocus
            style={{ flex: 1, padding: "8px 12px", fontSize: 13, border: `1px solid ${colors.border}`, borderRadius: 6, background: colors.bgCard, color: colors.text, fontFamily: fonts.body, outline: "none" }}
          />
          <button onClick={handleCreateBoard} disabled={!newBoardName.trim()} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: colors.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: fonts.body }}>Create</button>
          <button onClick={() => { setShowNewBoard(false); setNewBoardName(""); }} style={{ padding: "8px 12px", borderRadius: 6, border: `1px solid ${colors.border}`, background: "transparent", color: colors.textSecondary, cursor: "pointer" }}><X size={14} /></button>
        </div>
      )}

      {!selectedBoard && !loading && (
        <div style={{ textAlign: "center", padding: 60, color: colors.textSecondary, fontSize: 14 }}>
          No boards yet. Create one to get started.
        </div>
      )}

      {/* Calendar View */}
      {selectedBoard && viewMode === "calendar" && (
        <CalendarGrid
          items={calendarItems}
          onItemClick={(item) => setEditItem(items.find((i) => i.id === (item._originalId || item.id)) || item)}
          onDateClick={(date) => {
            setAddingInColumn("__calendar__");
            setAddDueDate(date.toISOString().split("T")[0]);
          }}
        />
      )}

      {/* Kanban / Cadence View */}
      {selectedBoard && (viewMode === "kanban" || viewMode === "cadence") && (
        <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 20, minHeight: 400 }}>
          {columns.map((col) => {
            const colItems = items
              .filter((i) => i.columnId === col.id)
              .sort((a, b) => (a.position || 0) - (b.position || 0));
            const isOver = dropTarget === col.id;
            const isSameCol = dragItem?.columnId === col.id;

            return (
              <div
                key={col.id}
                onDragOver={(e) => handleDragOver(e, col.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, col.id)}
                style={{
                  flex: "0 0 280px",
                  background: isOver ? "rgba(99,102,241,0.04)" : colors.bg,
                  borderRadius: 10,
                  border: `1px solid ${isOver ? "#6366f1" : colors.border}`,
                  display: "flex",
                  flexDirection: "column",
                  transition: "border-color 0.15s",
                }}
              >
                {/* Column header */}
                <div style={{ padding: "12px 14px 8px", display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: col.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: colors.text, fontFamily: fonts.body }}>{col.label}</span>
                  <span style={{ fontSize: 11, color: colors.textSecondary, marginLeft: "auto" }}>{colItems.length}</span>
                </div>

                {/* Items */}
                <div style={{ flex: 1, padding: "0 8px 8px", overflowY: "auto" }}>
                  {colItems.map((item, idx) => {
                    const typeInfo = KANBAN_ITEM_TYPES[item.type] || KANBAN_ITEM_TYPES.task;
                    const prioInfo = KANBAN_PRIORITIES[item.priority] || KANBAN_PRIORITIES.medium;
                    const showInsertBefore = (isOver || isSameCol) && dropIndex === idx;

                    return (
                      <div key={item.id}>
                        {showInsertBefore && <div style={{ height: 3, background: "#6366f1", borderRadius: 2, margin: "2px 0" }} />}
                        <div
                          ref={(el) => { cardRefs.current[item.id] = el; }}
                          draggable
                          onDragStart={(e) => handleDragStart(e, item)}
                          onDragEnd={handleDragEnd}
                          onClick={() => setEditItem(item)}
                          style={{
                            background: colors.bgCard,
                            border: `1px solid ${colors.border}`,
                            borderRadius: 8,
                            padding: "10px 12px",
                            marginBottom: 6,
                            cursor: "grab",
                            opacity: moving === item.id ? 0.5 : 1,
                            transition: "box-shadow 0.15s",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
                        >
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                            <div style={{ width: 4, height: 4, borderRadius: "50%", background: prioInfo.color, marginTop: 6, flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, lineHeight: 1.3, marginBottom: 4 }}>{item.title}</div>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 3, color: typeInfo.color, background: typeInfo.bg }}>{typeInfo.label}</span>
                                {item.dueDate && (
                                  <span style={{ fontSize: 10, color: dueDateColor(item.dueDate), display: "flex", alignItems: "center", gap: 2 }}>
                                    <Clock size={9} /> {fmtDate(item.dueDate)}
                                  </span>
                                )}
                                {item.recurrence && (
                                  <span style={{ fontSize: 9, color: colors.textSecondary }} title={describeRecurrence(item.recurrence)}>↻</span>
                                )}
                                {item.assignedTo && (
                                  <span style={{ fontSize: 10, color: colors.textSecondary }}>{item.assignedTo.split("@")[0]}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {(isOver || isSameCol) && dropIndex === colItems.length && (
                    <div style={{ height: 3, background: "#6366f1", borderRadius: 2, margin: "2px 0" }} />
                  )}

                  {/* Add item */}
                  {addingInColumn === col.id ? (
                    <div style={{ padding: 8, background: colors.bgCard, borderRadius: 8, border: `1px solid ${colors.border}`, marginTop: 4 }}>
                      <input
                        value={addTitle}
                        onChange={(e) => setAddTitle(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleAddItem(col.id)}
                        placeholder="Title..."
                        autoFocus
                        style={{ width: "100%", padding: "6px 8px", fontSize: 12, border: `1px solid ${colors.border}`, borderRadius: 5, background: colors.bg, color: colors.text, fontFamily: fonts.body, outline: "none", boxSizing: "border-box", marginBottom: 6 }}
                      />
                      <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                        <select value={addType} onChange={(e) => setAddType(e.target.value)} style={{ flex: 1, padding: "4px 6px", fontSize: 11, border: `1px solid ${colors.border}`, borderRadius: 5, background: colors.bg, color: colors.text, fontFamily: fonts.body }}>
                          {Object.entries(KANBAN_ITEM_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                        <input type="date" value={addDueDate} onChange={(e) => setAddDueDate(e.target.value)} style={{ flex: 1, padding: "4px 6px", fontSize: 11, border: `1px solid ${colors.border}`, borderRadius: 5, background: colors.bg, color: colors.text, fontFamily: fonts.body }} />
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => handleAddItem(col.id)} disabled={!addTitle.trim()} style={{ flex: 1, padding: "6px", borderRadius: 5, border: "none", background: colors.accent, color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Add</button>
                        <button onClick={() => { setAddingInColumn(null); setAddTitle(""); }} style={{ padding: "6px 10px", borderRadius: 5, border: `1px solid ${colors.border}`, background: "transparent", color: colors.textSecondary, cursor: "pointer", fontSize: 11 }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setAddingInColumn(col.id)}
                      style={{ width: "100%", padding: "8px", marginTop: 4, borderRadius: 6, border: `1px dashed ${colors.border}`, background: "transparent", color: colors.textSecondary, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4, fontFamily: fonts.body }}
                    >
                      <Plus size={12} /> Add item
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Calendar quick-add */}
      {addingInColumn === "__calendar__" && selectedBoard && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={() => setAddingInColumn(null)}>
          <div style={{ background: colors.bgCard, borderRadius: 12, padding: 24, width: 380, border: `1px solid ${colors.border}` }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700, color: colors.text }}>New Item</h3>
            <input value={addTitle} onChange={(e) => setAddTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { handleAddItem(columns[0]?.id || "col-1"); setAddingInColumn(null); } }} placeholder="Title..." autoFocus style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: `1px solid ${colors.border}`, borderRadius: 6, background: colors.bg, color: colors.text, fontFamily: fonts.body, outline: "none", boxSizing: "border-box", marginBottom: 8 }} />
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <select value={addType} onChange={(e) => setAddType(e.target.value)} style={{ flex: 1, padding: "6px 8px", fontSize: 12, border: `1px solid ${colors.border}`, borderRadius: 6, background: colors.bg, color: colors.text, fontFamily: fonts.body }}>
                {Object.entries(KANBAN_ITEM_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <input type="date" value={addDueDate} onChange={(e) => setAddDueDate(e.target.value)} style={{ flex: 1, padding: "6px 8px", fontSize: 12, border: `1px solid ${colors.border}`, borderRadius: 6, background: colors.bg, color: colors.text, fontFamily: fonts.body }} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { handleAddItem(columns[0]?.id || "col-1"); setAddingInColumn(null); }} disabled={!addTitle.trim()} style={{ flex: 1, padding: "8px", borderRadius: 6, border: "none", background: colors.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Add</button>
              <button onClick={() => setAddingInColumn(null)} style={{ padding: "8px 16px", borderRadius: 6, border: `1px solid ${colors.border}`, background: "transparent", color: colors.textSecondary, cursor: "pointer", fontSize: 13 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Item detail modal */}
      {editItem && (
        <ItemDetailModal
          item={editItem}
          columns={columns}
          onUpdate={(data) => handleUpdateItem(editItem.id, data)}
          onDelete={() => handleDeleteItem(editItem.id)}
          onClose={() => setEditItem(null)}
        />
      )}

      {itemsLoading && (
        <div style={{ position: "fixed", bottom: 20, right: 20, background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, padding: "8px 14px", display: "flex", alignItems: "center", gap: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
          <Loader2 size={14} className="spin" color={colors.accent} />
          <span style={{ fontSize: 12, color: colors.textSecondary }}>Loading...</span>
        </div>
      )}    </div>
  );
}

// ── Item Detail Modal ────────────────────────────────────────

function ItemDetailModal({ item, columns, onUpdate, onDelete, onClose }) {
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description || "");
  const [type, setType] = useState(item.type);
  const [columnId, setColumnId] = useState(item.columnId);
  const [dueDate, setDueDate] = useState(item.dueDate ? new Date(item.dueDate).toISOString().slice(0, 16) : "");
  const [recurrence, setRecurrence] = useState(item.recurrence || "");
  const [priority, setPriority] = useState(item.priority || "medium");
  const [assignedTo, setAssignedTo] = useState(item.assignedTo || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onUpdate({
      title, description, type, columnId,
      dueDate: dueDate ? new Date(dueDate).toISOString() : null,
      recurrence: recurrence || null,
      priority, assignedTo: assignedTo || null,
    });
    setSaving(false);
    onClose();
  };

  const inputStyle = {
    width: "100%", padding: "8px 10px", fontSize: 13, border: `1px solid ${colors.border}`,
    borderRadius: 6, background: colors.bg, color: colors.text, fontFamily: fonts.body, outline: "none", boxSizing: "border-box",
  };

  const labelStyle = { fontSize: 11, fontWeight: 600, color: colors.textSecondary, marginBottom: 4, display: "block" };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: colors.bgCard, borderRadius: 12, padding: 24, width: 480, maxHeight: "80vh", overflowY: "auto", border: `1px solid ${colors.border}`, boxShadow: "0 12px 40px rgba(0,0,0,0.15)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.text, fontFamily: fonts.heading }}>Edit Item</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: colors.textSecondary }}><X size={18} /></button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={labelStyle}>Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Type</label>
              <select value={type} onChange={(e) => setType(e.target.value)} style={inputStyle}>
                {Object.entries(KANBAN_ITEM_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Column</label>
              <select value={columnId} onChange={(e) => setColumnId(e.target.value)} style={inputStyle}>
                {columns.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Priority</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} style={inputStyle}>
                {Object.entries(KANBAN_PRIORITIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Assigned To</label>
              <input value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} placeholder="email@domain.com" style={inputStyle} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Due Date</label>
            <input type="datetime-local" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Recurrence</label>
            <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)} style={inputStyle}>
              {RECURRENCE_PRESETS.map((p) => <option key={p.label} value={p.value || ""}>{p.label}</option>)}
              {recurrence && !RECURRENCE_PRESETS.find((p) => p.value === recurrence) && (
                <option value={recurrence}>Custom: {recurrence}</option>
              )}
            </select>
            {recurrence && (
              <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}>{describeRecurrence(recurrence)}</div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button onClick={handleSave} disabled={saving || !title.trim()} style={{ flex: 1, padding: "10px", borderRadius: 6, border: "none", background: colors.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: fonts.body }}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
            <button onClick={onDelete} style={{ padding: "10px 16px", borderRadius: 6, border: `1px solid rgba(239,68,68,0.3)`, background: "rgba(239,68,68,0.05)", color: "#ef4444", fontSize: 13, cursor: "pointer", fontFamily: fonts.body }}>
              <Trash2 size={14} />
            </button>
          </div>

          {item.createdBy && (
            <div style={{ fontSize: 10, color: colors.textSecondary, textAlign: "center" }}>
              Created by {item.createdBy.split("@")[0]} · {fmtDateTime(item.createdAt)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
